/**
 * localStorage wrapper for the two pieces of client-side persisted state:
 *
 *  - reclaimToken — per-room secret issued by the server in `youAre`. This is
 *    the SOLE authority to reclaim a seat after a disconnect (ARCH §9.1).
 *    Stored keyed by room code so a player in two rooms doesn't collide.
 *  - displayName — last-used display name, prefilled in the join screen.
 *
 * Every call is wrapped in try/catch: a quota error, a Safari "Private
 * Browsing" mode, or a missing `window` (Cloudflare Pages prerender / vitest
 * without jsdom) must not crash the app. Failure is silent and the value
 * behaves as absent — the user just re-enters their name, and the server
 * issues a fresh reclaim token on next join (no game data is lost; the worst
 * case is a fresh seat).
 */

const RECLAIM_PREFIX = "hookum:reclaim:";
const NAME_KEY = "hookum:name";

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadReclaimToken(code: string): string | null {
  const s = safeStorage();
  if (!s) return null;
  try {
    return s.getItem(RECLAIM_PREFIX + code);
  } catch {
    return null;
  }
}

export function saveReclaimToken(code: string, token: string): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(RECLAIM_PREFIX + code, token);
  } catch {
    /* quota / private mode — ignore */
  }
}

export function clearReclaimToken(code: string): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.removeItem(RECLAIM_PREFIX + code);
  } catch {
    /* ignore */
  }
}

export function loadName(): string {
  const s = safeStorage();
  if (!s) return "";
  try {
    return s.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export function saveName(name: string): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(NAME_KEY, name);
  } catch {
    /* ignore */
  }
}
